import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  Box,
  Button,
  Card,
  Modal,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Chip
} from "@mui/material";
import { DeleteSharp, EditSharp, RemoveRedEye, Close } from "@mui/icons-material";
import { getPollChildren } from "src/api/commonapi";
import CustomLoader from "src/components/CustomLoader";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 1100,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 10,
  borderRadius: "10px",
  p: 2,
  overflow: "auto"
};

const P_TO_LABELS = {
  "0": "All",
  "7": "Orange Tick",
  "8": "Not Orange Tick"
};

export default function PollScheduleModal({
  open,
  onClose,
  parentRow,
  onEdit,
  onDelete,
  onViewResult
}) {
  const [children, setChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && parentRow?.p_id) fetchChildren(parentRow.p_id);
    if (!open) setChildren([]);
  }, [open, parentRow?.p_id]);

  const fetchChildren = async (pId) => {
    setIsLoading(true);
    try {
      const resp = await getPollChildren(pId);
      if (resp.status === 1) setChildren(resp.data || []);
    } catch (e) {
      console.error("Error fetching poll children:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { name: "#", width: "50px", selector: (_, idx) => idx + 1 },
    {
      name: "Type",
      width: "70px",
      cell: (row) => (row.is_main === 1 || row.is_main === "1" ? "Main" : "Sub")
    },
    {
      name: "Send Type",
      width: "110px",
      cell: (row) =>
        String(row.notification_send_type ?? row.poll_send_type) === "1"
          ? "Immediate"
          : "Scheduled"
    },
    { name: "Start Date", width: "110px", selector: (row) => row.s_date || "-" },
    { name: "End Date", width: "110px", selector: (row) => row.e_date || "-" },
    { name: "Send Date & Time", width: "180px", selector: (row) => row.n_date_time || "-" },
    {
      name: "To",
      width: "130px",
      cell: (row) => P_TO_LABELS[String(row.p_to)] || "-"
    },
    {
      name: "Show on Home",
      width: "110px",
      cell: (row) =>
        row.show_on_home === 1 || row.show_on_home === "1" ? "Yes" : "No"
    },
    {
      name: "Send Status",
      width: "100px",
      cell: (row) => (
        <Chip
              label={String(row.poll_send_status) === "1" ? "Sent" : "Unsent"}
          color={String(row.poll_send_status) === "1" ? "success" : "default"}
          size="small"
        />
      )
    },
    {
      name: "Action",
      width: "120px",
      cell: (row) => {
        return (
          <>
            <Tooltip title="Delete">
              <IconButton onClick={() => onDelete(row)} size="small">
                <DeleteSharp color="error" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                onClick={() =>
                  onEdit({
                    ...row,
                    // Attach the parent id explicitly so handleEdit can fetch the full
                    // batch (needed for the single "Send Immediately" slot check)
                    p_parent_id:
                      row.p_parent_id ?? row.parent_p_id ?? row.p_parentId ?? parentRow?.p_id,
                    // The children endpoint returns subs only, so the MAIN entry's send
                    // type has to come from the list row this modal was opened with.
                    parent_send_type:
                      parentRow?.notification_send_type ?? parentRow?.poll_send_type
                  })
                }
                size="small"
              >
                <EditSharp color="warning" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Result">
              <IconButton onClick={() => onViewResult(row.p_id)} size="small">
                <RemoveRedEye color="success" fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      }
    }
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">
            Schedule Entries — {parentRow?.p_question || `#${parentRow?.p_id}`}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
        <hr />
        <Card sx={{ mt: 1 }}>
          <DataTable
            columns={columns}
            data={children}
            progressPending={isLoading}
            progressComponent={<CustomLoader />}
            noHeader
            highlightOnHover
            pagination
            paginationPerPage={10}
          />
        </Card>
        <div style={{ margin: "10px auto 0", display: "flex", justifyContent: "center" }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
